import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import { MdNotifications, MdSearch, MdClose } from "react-icons/md";
// Reuse Components
import Pagination from "../../components/general/pagination/Pagination";
import StatCard from "../../components/admin/statcard/StatCard";
import ActionDropdown from "../../components/admin/dropdown/action/ActionDropdown";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import Button from "../../components/general/button/Button";
// CSS
import "../../assets/css/customer/customerDashboard/notifications/CustomerNotifications.css";
import MyContext from "../../context/MyContext";
import axios from "axios";
import { NotificationsPageSkeleton } from "../../components/general/skeleton/SkeletonBlock";
import { getEcho } from "../../components/general/echo/Echo";

// ── created_at ko "wall clock" string ki tarah treat karte hain (koi
// timezone conversion nahi) taake DB mein jo date/time save hai wahi
// frontend par bhi dikhe — browser/system ki local timezone follow
// nahi hoti is parsing mein, is liye "new Date(...)" bilkul use nahi
// karte in helpers ke andar. ──
const parseServerDateTime = (value) => {
  if (!value) return null;
  const match = String(value).match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/,
  );
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };
};

// Table/modal mein dikhane ke liye "YYYY-MM-DD"
const formatServerDate = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(
    p.day,
  ).padStart(2, "0")}`;
};

// Table/modal mein dikhane ke liye "hh:mm AM/PM"
const formatServerTime = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const ampm = p.hour >= 12 ? "PM" : "AM";
  return `${String(hour12).padStart(2, "0")}:${String(p.minute).padStart(
    2,
    "0",
  )} ${ampm}`;
};

// Edit form ke <input type="time"> field ke liye 24-hour "HH:mm"
const formatServerTime24 = (value) => {
  const p = parseServerDateTime(value);
  if (!p) return "";
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(
    2,
    "0",
  )}`;
};

function CustomerNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Notifications");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editStatus, setEditStatus] = useState("unread");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [saving, setSaving] = useState(false);
  const { baseUrl, authToken, loggedInCustomer } = useContext(MyContext);
  const [loading, setLoading] = useState(true);

  // Stats Data — status database mein 'unread'/'Unread'/garbage bhi ho sakta
  // hai, is liye "read" ke elawa sab kuch unread mana jata hai
  const stats = [
    {
      label: "Total Notifications",
      value: notifications.length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      badge: "All",
    },
    {
      label: "Unread",
      value: notifications
        .filter((n) => String(n.status).toLowerCase() !== "read")
        .length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      badge: "Pending",
    },
    {
      label: "Read",
      value: notifications
        .filter((n) => String(n.status).toLowerCase() === "read")
        .length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      badge: "Done",
    },
  ];

  // Filters
  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch =
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Notifications" ||
      (statusFilter === "Unread"
        ? String(notification.status).toLowerCase() !== "read"
        : String(notification.status).toLowerCase() === "read");
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = filteredNotifications.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const viewAllNotifications = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/notifications/viewAllNotifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setNotifications(response.data.notifications || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications.",
        });
      });
  };

  const changeNotificationStatus = (notifications) => {
    const formData = new FormData();
    formData.append("id", notifications.id);

    axios
      .post(
        `${baseUrl}/customer/notifications/changeNotificationStatus`,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification status successfully updated!",
          });
          viewAllNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const deleteNotification = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This notification will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(
            `${baseUrl}/customer/notifications/deleteNotification`,
            formData,
            { headers: { Authorization: `Bearer ${authToken}` } },
          )
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Notification deleted successfully!",
              });
              viewAllNotifications();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete notification.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete notifications.",
            });
          });
      }
    });
  };

  // ── Edit — created_at se date/time nikaal kar form fields
  // pre-fill karte hain (naive parsing, koi timezone conversion nahi) ──
  const openEditModal = (notification) => {
    setEditTitle(notification.title || "");
    setEditMessage(notification.message || "");
    setEditStatus(
      String(notification.status || "unread").toLowerCase() === "read"
        ? "read"
        : "unread",
    );
    setEditDate(formatServerDate(notification.created_at));
    setEditTime(formatServerTime24(notification.created_at));
    setSelectedNotification(notification);
    setShowModal(true);
    setIsEditing(true);
  };

  const saveEditedNotification = () => {
    if (!selectedNotification) return;
    if (!editTitle.trim() || !editMessage.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Title aur message dono zaroori hain.",
      });
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append("id", selectedNotification.id);
    formData.append("title", editTitle);
    formData.append("message", editMessage);
    formData.append("status", editStatus);
    formData.append("date", editDate);
    formData.append("time", editTime);

    axios
      .post(`${baseUrl}/customer/notifications/editNotification`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSaving(false);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification updated successfully!",
          });
          setShowModal(false);
          setIsEditing(false);
          viewAllNotifications();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message || "Failed to update notification.",
          });
        }
      })
      .catch((error) => {
        setSaving(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update notification.",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/customer/login");
      return;
    }
    viewAllNotifications();
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !loggedInCustomer?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `customer.${loggedInCustomer.id}`;
    const channel = echo.private(channelName);

    channel.listen(".new-notification", (e) => {
      const incoming = e.notification;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInCustomer?.id]);

  if (loading) {
    return (
      <CustomerMasterLayout>
        <NotificationsPageSkeleton />
      </CustomerMasterLayout>
    );
  }

  return (
    <CustomerMasterLayout>
      <div className="customer-notifications-page">
        <Container fluid>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={3}>
                <StatCard {...stat} />
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Row className="mb-3">
            <Col xs={12}>
              <div className="customer-filters-wrapper">
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={8} lg={9}>
                    <div className="customer-search-box">
                      <span className="customer-search-icon">
                        <MdSearch />
                      </span>
                      <input
                        type="text"
                        className="customer-form-control"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <select
                      className="customer-form-control customer-select"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>All Notifications</option>
                      <option>Unread</option>
                      <option>Read</option>
                    </select>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Notifications Table */}
          <Row>
            <Col xs={12}>
              <div className="customer-table-responsive">
                <table className="customer-table">
                  <thead className="customer-table-head">
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="customer-table-body">
                    {currentNotifications.map((n) => (
                      <tr
                        key={n.id}
                        className={
                          String(n.status).toLowerCase() !== "read"
                            ? "customer-unread-row"
                            : ""
                        }
                      >
                        <td>{n.id}</td>
                        <td>{n.title}</td>
                        <td className="customer-message-preview">
                          {n.message}
                        </td>
                        <td>{formatServerDate(n.created_at || n.date)}</td>
                        <td>{formatServerTime(n.created_at)}</td>
                        <td>
                          <span
                            className={`customer-badge ${String(n.status).toLowerCase() === "read" ? "customer-badge-success" : "customer-badge-warning"}`}
                          >
                            {n.status}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            actions={[
                              {
                                label: "View",
                                onClick: () => {
                                  setSelectedNotification(n);
                                  setIsEditing(false);
                                  setShowModal(true);
                                },
                                className: "view",
                              },
                              {
                                label: "Edit",
                                onClick: () => openEditModal(n),
                                className: "edit",
                              },
                              {
                                label:
                                  String(n.status).toLowerCase() === "read"
                                    ? "Mark as Unread"
                                    : "Mark as Read",
                                onClick: () => {
                                  changeNotificationStatus(n);
                                },
                                className: "enable",
                              },
                              {
                                label: "Delete",
                                onClick: () => {
                                  deleteNotification(n.id);
                                },
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredNotifications.length === 0 && (
                  <div className="customer-no-data">No notifications found</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Pagination */}
          <Row>
            <Col xs={12}>
              <Pagination
                totalItems={filteredNotifications.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
        </Container>

        {/* Notification Modal — view ya edit, isEditing decide karta hai */}
        {showModal && selectedNotification && (
          <div
            className="customer-modal-overlay"
            onClick={() => {
              setShowModal(false);
              setIsEditing(false);
            }}
          >
            <div
              className="customer-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="customer-modal-header">
                <h3 className="customer-modal-title">
                  {isEditing ? "Edit Notification" : "Notification Details"}
                </h3>
                <button
                  className="customer-modal-close"
                  onClick={() => {
                    setShowModal(false);
                    setIsEditing(false);
                  }}
                >
                  <MdClose />
                </button>
              </div>

              {isEditing ? (
                <>
                  <div className="customer-modal-body">
                    <div className="mb-3">
                      <label className="customer-form-label">
                        Notification ID
                      </label>
                      <input
                        type="text"
                        className="customer-form-control"
                        value={selectedNotification.id}
                        disabled
                        readOnly
                      />
                    </div>

                    <div className="mb-3">
                      <label className="customer-form-label">Title</label>
                      <input
                        type="text"
                        className="customer-form-control"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="customer-form-label">Message</label>
                      <textarea
                        className="customer-form-control"
                        rows={3}
                        value={editMessage}
                        onChange={(e) => setEditMessage(e.target.value)}
                      />
                    </div>

                    <Row className="g-3">
                      <Col xs={6}>
                        <label className="customer-form-label">Date</label>
                        <input
                          type="date"
                          className="customer-form-control"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </Col>

                      <Col xs={6}>
                        <label className="customer-form-label">Time</label>
                        <input
                          type="time"
                          className="customer-form-control"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                        />
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <label className="customer-form-label">Status</label>
                      <select
                        className="customer-form-control customer-select"
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                      >
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                      </select>
                    </div>
                  </div>

                  <div className="customer-modal-footer">
                    <button
                      className="custom-btn custom-btn-outline"
                      onClick={() => {
                        setShowModal(false);
                        setIsEditing(false);
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </button>

                    <button
                      className="custom-btn custom-btn-primary"
                      onClick={saveEditedNotification}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="customer-modal-body">
                    <p>
                      <strong>Title:</strong> {selectedNotification.title}
                    </p>
                    <p>
                      <strong>Message:</strong> {selectedNotification.message}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatServerDate(selectedNotification.created_at)} at{" "}
                      {formatServerTime(selectedNotification.created_at)}
                    </p>
                    <p>
                      <strong>Status:</strong> {selectedNotification.status}
                    </p>
                  </div>
                  <div className="customer-modal-footer">
                    <button
                      className="custom-btn custom-btn-primary"
                      onClick={() => openEditModal(selectedNotification)}
                    >
                      Edit
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </CustomerMasterLayout>
  );
}

export default CustomerNotifications;
